import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { API_URL, setMaintenanceHandler } from '../../services/api';
import Logo from './Logo';

const POLL_MS = 5000;

/**
 * Shown over the open app while the server is under maintenance (a release): requests answered with
 * 503 MAINTENANCE or 502/504 open it. It checks the API every few seconds; once the server answers it offers
 * to reload (new version) or to continue, so nothing typed into a form is lost by an automatic reload.
 */
export default function MaintenanceOverlay() {
    const { t } = useTranslation();
    const [state, setState] = useState(null);       // null | 'waiting' | 'done'

    useEffect(() => {
        setMaintenanceHandler(() => setState(s => (s === 'waiting' ? s : 'waiting')));
        return () => setMaintenanceHandler(null);
    }, []);

    useEffect(() => {
        if (state !== 'waiting') return undefined;
        const id = setInterval(() => {
            fetch(`${API_URL}/api/health`, { cache: 'no-store' })
                .then(r => { if (r.ok) setState('done'); })
                .catch(() => {});
        }, POLL_MS);
        return () => clearInterval(id);
    }, [state]);

    if (!state) return null;
    const done = state === 'done';
    return (
        <Box role="alertdialog" aria-live="polite"
             sx={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
                   bgcolor: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(3px)' }}>
            <Paper elevation={0} sx={{ width: '100%', maxWidth: 440, borderRadius: '20px', p: { xs: 3, sm: 4 }, textAlign: 'center', border: '1px solid #E2E8F0' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}><Logo size={40} /></Box>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#0F172A', mb: 1 }}>
                    {done ? t('maintenance.doneTitle') : t('maintenance.title')}
                </Typography>
                <Typography sx={{ fontSize: 14.5, color: '#64748B', lineHeight: 1.6 }}>
                    {done ? t('maintenance.doneText') : t('maintenance.text')}
                </Typography>
                {done ? (
                    <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', mt: 3, flexWrap: 'wrap' }}>
                        <Button onClick={() => setState(null)} sx={{ color: '#64748B' }}>{t('maintenance.continue')}</Button>
                        <Button variant="contained" disableElevation onClick={() => window.location.reload()}>{t('maintenance.reload')}</Button>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25, mt: 3, color: '#64748B' }}>
                        <CircularProgress size={16} />
                        <Typography sx={{ fontSize: 13 }}>{t('maintenance.checking')}</Typography>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
